import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  isValidSocialAuthMessage,
  SocialAuthStatus,
  SocialLoginOption,
} from "@redotech/redo-model/auth/social-auth";
import { alertMessage, alertOnFailure } from "@redotech/redo-web/alert";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { memo, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { SetAuthContext } from "../contexts/auth";
import { SetUserContext } from "../contexts/user";
import * as styles from "./google-sign-in-button.module.css";

/**
 * GoogleSignInButton
 *
 * This component is used to sign in with Google. By default, it will login
 * the user and redirect to their latest order if they have one, or the all
 * orders page if they do not.
 *
 * redirectUri?: string
 *
 * If provided, the user will be redirected to the provided URI after login.
 */
const GoogleSignInButton = memo(function GoogleSignInButton({
  nextUrl,
}: {
  nextUrl?: string;
}) {
  const { isLoading, setSocialAuthCode } = useLoginWithGoogle({ nextUrl });
  useSocialMessageListener({ setSocialAuthCode });
  const googleAuthInitiatorUrl = process.env.RETURNS_APP_URL
    ? `${process.env.RETURNS_APP_URL}/auth/google-auth`
    : "https://returns.getredo.com/auth/google-auth";

  const handleClick = () => {
    // Open a popup window for Google Auth
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      googleAuthInitiatorUrl,
      "redo-google-auth",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`,
    );
  };

  return (
    <>
      {isLoading &&
        createPortal(
          <div className={styles.loadingOverlay}>
            <LoadingRedoAnimation />
          </div>,
          document.body,
        )}
      <button className={styles.googleButton} onClick={handleClick}>
        <svg
          className={styles.googleIcon}
          height="18"
          viewBox="0 0 48 48"
          width="18"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            fill="#EA4335"
          />
          <path
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            fill="#4285F4"
          />
          <path
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            fill="#FBBC05"
          />
          <path
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            fill="#34A853"
          />
        </svg>
        <span className={styles.googleButtonText}>Sign in with Google</span>
      </button>
    </>
  );
});

/**
 * useLoginWithGoogle
 *
 * This hook is used to login the user using Google Auth, and redirecting them after login.
 * The code must be set using the setSocialAuthCode function, usually retrieved from the popup window.
 */
const useLoginWithGoogle = ({ nextUrl }: { nextUrl?: string }) => {
  const navigate = useNavigate();
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const rpcClient = useContext(RpcClientContext);
  const [isLoading, setIsLoading] = useState(false);
  const [socialAuthCode, setSocialAuthCode] = useState<string | null>();

  useEffect(() => {
    if (!rpcClient || !setAuth_ || !setUser || !socialAuthCode) {
      return;
    }

    // Show loading state while converting the token
    setIsLoading(true);

    // Call the RPC endpoint to convert the social auth code to a customer auth token
    void alertOnFailure("Authentication failed")(async () => {
      try {
        const response = await rpcClient.convertSocialAuthToCustomerAuth({
          provider: SocialLoginOption.GOOGLE,
          code: socialAuthCode,
        });

        if (
          response.customerAuthToken &&
          response.customer &&
          !response.error
        ) {
          setAuth_(response.customerAuthToken);
          setUser(response.customer);
          let defaultRedirectUrl = "/returns-portal/orders/";

          if (response.orderId) {
            defaultRedirectUrl = `/returns-portal/orders/${response.orderId}`;
          } else if (response.warrantyId) {
            defaultRedirectUrl = `/returns-portal/warranties/${response.warrantyId}/warranty`;
          }

          const path = nextUrl || defaultRedirectUrl;
          navigate(path, { replace: true });
        } else if (response.error) {
          // Most likely just the user does not have a matching email address on store
          setIsLoading(false);
          throw new Error(response.error);
        }
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    }).catch(() => {
      setIsLoading(false);
    });

    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialAuthCode]);

  return { isLoading, setSocialAuthCode };
};

const useSocialMessageListener = ({
  setSocialAuthCode,
}: {
  setSocialAuthCode: (code: string) => void;
}) => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const message = event.data;

      if (!isValidSocialAuthMessage(message)) {
        return;
      }

      if (message.status === SocialAuthStatus.Success && message.code) {
        if (setSocialAuthCode) {
          setSocialAuthCode(message.code);
        }
      } else if (message.status === SocialAuthStatus.NoAuthorizationCode) {
        alertMessage("Could not login with Google. Please try again.");
      } else if (message.status !== SocialAuthStatus.Success) {
        alertMessage("Could not login with Google. Please try again.");
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [setSocialAuthCode]);
};

export { GoogleSignInButton, useLoginWithGoogle, useSocialMessageListener };
